import { ColorType } from 'lightweight-charts'
import { getChartColors } from './chart_theme'

// CHART_COLORS used to be a static const. It's now a Proxy that reads live
// CSS variable values on every property access, so existing call sites like
// `CHART_COLORS.background` resolve to the current theme's colour without
// callers needing to refactor.
export const CHART_COLORS = new Proxy({}, {
  get(_target, prop) {
    return getChartColors()[prop]
  }
})

// Default moving average colors by period
export const MA_COLORS = {
  21: '#FF6B6B',   // Red - 21-day
  50: '#2196F3',   // Blue - 50-day
  150: '#FF9800',  // Orange - 150-day
  200: '#9C27B0'   // Purple - 200-day
}

// The IBD-style RS line (price relative to the benchmark index) and the
// overlay price scale it lives on. Like MA_COLORS this is a fixed indicator
// colour rather than a theme surface (the theme-driven colours are the
// CHART_COLORS proxy above, which reads CSS variables; an indicator line is
// not one of those). Slate, not blue: the four default moving averages
// already own the blues, and IBD's signature blue is kept for the DOTS.
export const RS_LINE_COLOR = '#64748b'
export const RS_LINE_DOT_COLOR = '#3b82f6'
export const RS_LINE_PRICE_SCALE_ID = 'rs'

/**
 * Get default color for a moving average period
 * @param {number} period - MA period
 * @returns {string} Color hex code
 */
export function getDefaultMaColor(period) {
  return MA_COLORS[period] || '#888888'
}

/**
 * Split a server ma_overlay key ("ema_21", "sma_50") into its parts.
 * @param {string} key - "<type>_<period>"
 * @returns {{type: string, period: number}} type upper-cased for display
 */
export function parseMaOverlayKey(key) {
  const [ type, period ] = String(key).split('_')
  return { type: (type || '').toUpperCase(), period: Number(period) }
}

/**
 * Calculate Exponential Moving Average (EMA)
 * @param {Array} data - Array of OHLCV objects with {time, close}
 * @param {number} period - EMA period
 * @returns {Array} Array of {time, value} objects
 */
export function calculateEMA(data, period) {
  const result = []
  const multiplier = 2 / (period + 1)
  let ema = null

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      // Whitespace data points until we have enough data
      result.push({ time: data[i].time })
    } else if (i === period - 1) {
      // First EMA value uses SMA
      let sum = 0
      for (let j = 0; j < period; j++) {
        sum += data[i - j].close
      }
      ema = sum / period
      result.push({ time: data[i].time, value: ema })
    } else {
      // EMA = Close * multiplier + EMA(prev) * (1 - multiplier)
      ema = (data[i].close * multiplier) + (ema * (1 - multiplier))
      result.push({ time: data[i].time, value: ema })
    }
  }

  return result
}

/**
 * Calculate Simple Moving Average (SMA)
 * @param {Array} data - Array of OHLCV objects with {time, close}
 * @param {number} period - SMA period
 * @returns {Array} Array of {time, value} objects
 */
export function calculateSMA(data, period) {
  const result = []

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      // Whitespace data points until we have enough data
      result.push({ time: data[i].time })
    } else {
      let sum = 0
      for (let j = 0; j < period; j++) {
        sum += data[i - j].close
      }
      result.push({ time: data[i].time, value: sum / period })
    }
  }

  return result
}

/**
 * Map OHLCV data to volume histogram data with colors
 * @param {Array} data - Array of OHLCV objects
 * @returns {Array} Array of {time, value, color} objects
 */
export function buildVolumeData(data, upColor = null, downColor = null) {
  const up = upColor || CHART_COLORS.up
  const down = downColor || CHART_COLORS.down
  return data.map(item => ({
    time: item.time,
    value: item.volume || 0,
    color: item.close >= item.open ? up : down
  }))
}

/**
 * Build watermark lines configuration from stock info
 * @param {Object} stockInfo - Stock information object
 * @returns {Array} Array of watermark line configurations
 */
export function buildWatermarkLines(stockInfo) {
  if (!stockInfo) return []

  const lines = []

  // Use text colour, alpha-blended into the background, so the watermark
  // stays a soft echo in either theme rather than dark-on-dark.
  const watermark = (alpha) => {
    const text = getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
    return text ? `color-mix(in srgb, ${text} ${Math.round(alpha * 100)}%, transparent)` : `rgba(0, 0, 0, ${alpha})`
  }

  if (stockInfo.ticker) {
    lines.push({
      text: stockInfo.ticker,
      color: watermark(0.15),
      fontSize: 72,
      fontStyle: 'bold'
    })
  }

  if (stockInfo.name) {
    lines.push({
      text: stockInfo.name,
      color: watermark(0.12),
      fontSize: 24
    })
  }

  if (stockInfo.industry_group) {
    lines.push({
      text: stockInfo.industry_group,
      color: watermark(0.10),
      fontSize: 16
    })
  }

  lines.push({
    text: 'relativestrength.com.au',
    color: watermark(0.10),
    fontSize: 14
  })

  return lines
}

/**
 * Build base chart options configuration
 * @param {Object} options - Override options
 * @returns {Object} Chart options object
 */
export function buildChartOptions(options = {}) {
  const {
    width = 800,
    height = 400,
    logScale = false,
    interactive = true,
    bottomMargin = 0.25
  } = options

  return {
    width,
    height,
    layout: {
      background: { type: ColorType.Solid, color: CHART_COLORS.background },
      textColor: CHART_COLORS.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    grid: {
      vertLines: { color: CHART_COLORS.grid },
      horzLines: { color: CHART_COLORS.grid },
    },
    timeScale: {
      timeVisible: false,
      secondsVisible: false,
      borderColor: CHART_COLORS.border,
      rightOffset: 5,
      barSpacing: 10,
      minBarSpacing: 5,
      fixLeftEdge: true,
      localization: {
        locale: 'en-US',
        dateFormat: "dd MMM 'yy",
      },
    },
    rightPriceScale: {
      borderColor: CHART_COLORS.border,
      visible: true,
      autoScale: true,
      mode: logScale ? 1 : 0,
      scaleMargins: {
        top: 0.1,
        bottom: bottomMargin,
      },
    },
    crosshair: {
      mode: interactive ? 1 : 0,
    },
    handleScroll: interactive,
    handleScale: interactive,
  }
}

/**
 * Build candlestick series options
 * @returns {Object} Candlestick series options
 */
export function buildCandlestickOptions(upColor = null, downColor = null) {
  const up = upColor || CHART_COLORS.up
  const down = downColor || CHART_COLORS.down
  return {
    upColor: up,
    downColor: down,
    borderVisible: false,
    wickUpColor: up,
    wickDownColor: down,
    priceScaleId: 'right',
  }
}

// Style options for the user-selectable main price series. The series
// *class* stays with the callers (this module deliberately never imports
// lightweight-charts — Jest suites lean on it staying pure).
export function buildPriceSeriesStyleOptions(chartType, upColor = null, downColor = null) {
  const up = upColor || CHART_COLORS.up
  const down = downColor || CHART_COLORS.down
  switch (chartType) {
    case 'bar':
      return { upColor: up, downColor: down }
    case 'line':
      // Without a custom color, keep the historical blue line
      return { color: upColor || '#2962FF', lineWidth: 2 }
    case 'area':
      return upColor
        ? { topColor: hexToRgba(upColor, 0.56), bottomColor: hexToRgba(upColor, 0.04), lineColor: upColor, lineWidth: 2 }
        : { topColor: 'rgba(33, 150, 243, 0.56)', bottomColor: 'rgba(33, 150, 243, 0.04)', lineColor: 'rgba(33, 150, 243, 1)', lineWidth: 2 }
    default:
      return buildCandlestickOptions(upColor, downColor)
  }
}

/** App-wide default price precision — two decimals, as the ASX has always used. */
export const DEFAULT_PRICE_PRECISION = 2
const MAX_PRICE_PRECISION = 8

/**
 * Price formatting for a security at its own precision.
 *
 * ASX closes are two decimals and this returns exactly lightweight-charts'
 * own default for them, so nothing about an equity chart changes. Crypto
 * carries a per-coin scale (stocks.price_scale, sent as stock.price_scale on
 * the chart payload): a sub-cent token drawn at two decimals is a flat line
 * on the axis and an unreadable crosshair.
 *
 * @param {number} precision - decimal places, 0-8
 * @returns {Object} lightweight-charts priceFormat
 */
export function buildPriceFormat(precision = DEFAULT_PRICE_PRECISION) {
  const digits = Number.isFinite(Number(precision))
    ? Math.min(Math.max(Math.trunc(Number(precision)), 0), MAX_PRICE_PRECISION)
    : DEFAULT_PRICE_PRECISION
  return { type: 'price', precision: digits, minMove: 10 ** -digits }
}

/**
 * Build volume series options
 *
 * type: 'volume' is lightweight-charts' own abbreviating formatter (1.2K /
 * 3.4M / 5.6B), which is what keeps crypto's dollar-notional volumes — in the
 * billions, where an ASX share count is in the millions — inside the axis.
 * @returns {Object} Volume series options
 */
export function buildVolumeSeriesOptions(upColor = null) {
  return {
    color: upColor || CHART_COLORS.up,
    priceFormat: {
      type: 'volume',
      precision: 0,
      minMove: 1,
    },
    priceScaleId: 'volume',
  }
}

/**
 * Build volume scale options
 * @returns {Object} Volume price scale options
 */
export function buildVolumeScaleOptions() {
  return {
    scaleMargins: {
      top: 0.8,
      bottom: 0.0,
    },
    visible: true,
    borderVisible: true,
  }
}

/**
 * Build line series options for moving averages
 * @param {Object} config - MA configuration {color, lineWidth}
 * @returns {Object} Line series options
 */
export function buildMaSeriesOptions(config = {}) {
  return {
    color: config.color || '#888888',
    lineWidth: config.lineWidth || 1,
    priceLineVisible: false,
    lastValueVisible: false,
    // Same precision as the price series it overlays: an MA rounded to two
    // decimals over a four-decimal candle plots as a staircase.
    priceFormat: buildPriceFormat(config.precision),
  }
}

/**
 * Build line series options for the RS (relative strength) overlay line.
 *
 * It rides its OWN overlay price scale (`RS_LINE_PRICE_SCALE_ID`) because the
 * plotted value is a ratio × 100 — an arbitrary scale that would wreck the
 * price pane's autoscale if it shared the right-hand one. The scale itself is
 * hidden and positioned by the caller (scaleMargins), since only the caller
 * knows whether volume is drawn below it. No last-value badge and no price
 * line: the number means nothing on its own, the SHAPE is the whole signal.
 *
 * @param {Object} config - {color, lineWidth} overrides
 * @returns {Object} Line series options
 */
export function buildRsLineSeriesOptions(config = {}) {
  return {
    color: config.color || RS_LINE_COLOR,
    lineWidth: config.lineWidth || 1.5,
    priceScaleId: RS_LINE_PRICE_SCALE_ID,
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: true,
  }
}

// ---------------------------------------------------------------------------
// Trend Gauges (Pressure / Buy Risk / TPR) — our best-effort reconstruction
// of indicators publicly associated with Mark Minervini's TradingView tools.
// See docs/features/TREND_GAUGES.md for the formulas/assumptions behind the
// values these colors represent.
// ---------------------------------------------------------------------------

const TREND_GAUGE_COLORS = {
  buy_risk_state: {
    low: '#22c55e',      // green — 0-15% above the 50MA (buy zone)
    caution: '#eab308',  // yellow — 15-25% above
    extended: '#f97316', // orange — >25% above
    broken: '#ef4444',   // red — below the 50MA
  },
  pressure_state: {
    accumulation: '#22c55e',
    neutral: '#9ca3af',
    distribution: '#ef4444',
  },
}

/**
 * Color for a single trend-gauge state value.
 * @param {'buy_risk_state'|'pressure_state'} kind
 * @param {string|null} state
 * @returns {string|null} hex color, or null when the state is unknown
 */
function trendGaugeStateColor(kind, state) {
  return TREND_GAUGE_COLORS[kind]?.[state] || null
}

/**
 * Color for a TPR row, banded by score (0-8): a full pass (8/8, all
 * criteria including RS leadership) is green, a near-miss (6-7) is yellow,
 * anything further off is red.
 * @param {number|null} score
 * @returns {string|null}
 */
export function tprScoreColor(score) {
  if (score == null) return null
  if (score >= 8) return '#22c55e'
  if (score >= 6) return '#eab308'
  return '#ef4444'
}

/**
 * Color for one trend_gauges payload row, for any gauge kind.
 * @param {Object} row - a row from the chart_data trend_gauges array
 * @param {'buy_risk_state'|'pressure_state'|'tpr'} kind
 * @returns {string|null}
 */
function trendGaugeRowColor(row, kind) {
  return kind === 'tpr' ? tprScoreColor(row.tpr_score) : trendGaugeStateColor(kind, row[kind])
}

// The "all clear" state color shared by every gauge (accumulation / low buy
// risk / full 8-point TPR pass). The header pills compare against it to
// decide whether all three gauges are currently green.
export const TREND_GAUGE_POSITIVE_COLOR = '#22c55e'

/**
 * Color for a gauge's most recent known state — used for the pane's y-axis
 * name badge and the chart-header pills. Walks back past trailing null
 * states (e.g. not enough history for the 50MA yet) rather than treating
 * them as a state of their own.
 * @param {Array<Object>} trendGauges - rows from the chart_data response
 * @param {'buy_risk_state'|'pressure_state'|'tpr'} kind
 * @returns {string|null}
 */
export function latestTrendGaugeColor(trendGauges, kind) {
  if (!trendGauges?.length) return null
  for (let i = trendGauges.length - 1; i >= 0; i--) {
    const color = trendGaugeRowColor(trendGauges[i], kind)
    if (color) return color
  }
  return null
}

/**
 * Black-or-white text color for readable contrast on a solid hex background
 * (the yellow/orange gauge states need dark text; the rest need light).
 * @param {string} hexColor - '#rrggbb'
 * @returns {string}
 */
export function contrastTextColor(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16)
  const g = parseInt(hexColor.slice(3, 5), 16)
  const b = parseInt(hexColor.slice(5, 7), 16)
  // Perceived luminance (ITU-R BT.601)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#111827' : '#ffffff'
}

/**
 * Group a trend_gauges API payload into contiguous same-color runs for one
 * gauge — lightweight {color, fromDate, toDate} descriptors consumed by
 * the TrendGaugeBand canvas primitive (trend_gauge_band.js), which paints
 * each run as a single filled rectangle. Rows with no state (not enough
 * history yet, e.g. no 50MA) break the run rather than being bridged over
 * with a misleading color.
 *
 * @param {Array<Object>} trendGauges - rows from the chart_data response
 * @param {'buy_risk_state'|'pressure_state'|'tpr'} kind
 * @returns {Array<{color: string, fromDate: string, toDate: string}>}
 */
export function buildTrendGaugeBands(trendGauges, kind) {
  return collapseColorBands(trendGauges, row => trendGaugeRowColor(row, kind))
}

/**
 * Collapse per-date rows into contiguous same-color bands. The one run-length
 * loop behind both the trend-gauge strips and the Weinstein stage tint — a
 * row whose color resolves to null breaks the run (a real gap in the data,
 * never bridged with a misleading color).
 *
 * @param {Array<{time: string}>} rows
 * @param {(row: object) => string|null} colorFor
 * @returns {Array<{color: string, fromDate: string, toDate: string}>}
 */
function collapseColorBands(rows, colorFor) {
  if (!rows?.length) return []

  const bands = []
  let current = null // { color, fromDate, toDate }

  rows.forEach(row => {
    const color = colorFor(row)

    if (!color) {
      if (current) bands.push(current) // flush the pending band first
      current = null
      return
    }

    if (current && current.color === color) {
      current.toDate = row.time
      return
    }

    if (current) bands.push(current)
    current = { color, fromDate: row.time, toDate: row.time }
  })

  if (current) bands.push(current)
  return bands
}

/**
 * Collapse a per-date Weinstein stage series into contiguous same-stage
 * colored bands, mirroring buildTrendGaugeBands. The stage colors are the
 * --app-stage-*-line palette variables (resolved by the caller via
 * `resolveColor`, which needs DOM access this module deliberately avoids),
 * softened to a background tint with `alpha`.
 *
 * A nil-stage row (young listing, head of the EMA window) breaks the run —
 * don't bridge a real gap with a guessed color.
 *
 * @param {Array<{time: string, stage: string|null}>} stages
 * @param {(stage: string) => string|null} resolveColor
 * @returns {Array<{color: string, fromDate: string, toDate: string}>}
 */
export function buildStageBands(stages, resolveColor) {
  return collapseColorBands(stages, row => (row.stage ? resolveColor(row.stage) : null))
}

/**
 * "stage_2b" -> "2b" — the token used in the --app-stage-2b-* CSS variable
 * names and the .stage-chip--2b classes, single-sourced from the stage key.
 */
export function stageCssCode(stageKey) {
  return stageKey ? stageKey.replace(/^stage_/, '') : null
}

/**
 * #rrggbb -> rgba(r, g, b, alpha). Canvas fillStyle needs a concrete color —
 * the palette variables hold plain hex, and color-mix support on canvas is
 * spottier than in CSS.
 */
export function hexToRgba(hex, alpha) {
  const match = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim())
  if (!match) return null

  const value = parseInt(match[1], 16)
  const r = (value >> 16) & 0xff
  const g = (value >> 8) & 0xff
  const b = value & 0xff
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
